import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@better-bookkeeping/ui/button";
import { Alert, AlertDescription } from "@better-bookkeeping/ui/alert";
import { Badge } from "@better-bookkeeping/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@better-bookkeeping/ui/alert-dialog";
import {
  AlertCircle,
  ArrowLeft,
  Clock,
  Laptop,
  ShieldCheck,
  Smartphone,
  Trash2,
} from "lucide-react";
import { useMemo, useState } from "react";
import { orpcClient } from "@/lib/auth-client";
import { AuthLoading } from "@/components/auth-loading";
import { formatRelative } from "@/lib/relative-time";

export const Route = createFileRoute("/sessions")({
  component: SessionsPage,
});

function describeDevice(userAgent: string | null | undefined): {
  label: string;
  icon: typeof Laptop;
} {
  if (!userAgent) return { label: "Unknown device", icon: Laptop };

  const browser = userAgent.includes("Firefox")
    ? "Firefox"
    : userAgent.includes("Edg/")
      ? "Edge"
      : userAgent.includes("Chrome")
        ? "Chrome"
        : userAgent.includes("Safari")
          ? "Safari"
          : "Browser";

  if (
    userAgent.includes("Mobile") ||
    userAgent.includes("iPhone") ||
    userAgent.includes("Android")
  ) {
    if (userAgent.includes("iPhone")) return { label: `${browser} on iPhone`, icon: Smartphone };
    if (userAgent.includes("Android")) return { label: `${browser} on Android`, icon: Smartphone };
    return { label: `${browser} on mobile`, icon: Smartphone };
  }
  if (userAgent.includes("Windows")) return { label: `${browser} on Windows`, icon: Laptop };
  if (userAgent.includes("Mac OS X")) return { label: `${browser} on macOS`, icon: Laptop };
  if (userAgent.includes("Linux")) return { label: `${browser} on Linux`, icon: Laptop };
  return { label: browser, icon: Laptop };
}

function SessionsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<{ id: string; label: string } | null>(null);
  const [confirmAllOpen, setConfirmAllOpen] = useState(false);

  const sessionsQuery = useQuery({
    queryKey: ["auth", "sessions"],
    queryFn: () => orpcClient.auth.listSessions(),
    retry: false,
  });

  const sessions = sessionsQuery.data ?? [];
  const otherSessionCount = useMemo(
    () => sessions.filter((session) => !session.current).length,
    [sessions],
  );

  const revokeSession = useMutation({
    mutationFn: (sessionId: string) => orpcClient.auth.revokeSession({ sessionId }),
    onSuccess: async () => {
      setError(null);
      setRevokeTarget(null);
      await queryClient.invalidateQueries({ queryKey: ["auth", "sessions"] });
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : "We couldn't revoke that session. Try again.");
    },
  });

  const revokeOtherSessions = useMutation({
    mutationFn: () => orpcClient.auth.revokeOtherSessions(),
    onSuccess: async () => {
      setError(null);
      setConfirmAllOpen(false);
      await queryClient.invalidateQueries({ queryKey: ["auth", "sessions"] });
    },
    onError: (err) => {
      setError(
        err instanceof Error ? err.message : "We couldn't revoke those sessions. Try again.",
      );
    },
  });

  if (sessionsQuery.isLoading) {
    return <AuthLoading data-testid="sessions-loading" />;
  }

  if (sessionsQuery.isError) {
    return (
      <Alert variant="destructive" data-testid="sessions-load-error">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>We couldn't load your sessions. Refresh to try again.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate({ to: "/" })}
          className="-ml-2"
          data-testid="sessions-back-button"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <div className="space-y-1.5">
          <h2 className="text-2xl font-semibold tracking-tight" data-testid="sessions-title">
            Where you're signed in
          </h2>
          <p className="text-sm text-muted-foreground">
            Spot anything you don't recognize? Revoke it.
          </p>
        </div>
      </div>

      {error && (
        <Alert variant="destructive" data-testid="sessions-error">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {otherSessionCount > 0 && (
        <Button
          variant="outline"
          onClick={() => setConfirmAllOpen(true)}
          disabled={revokeOtherSessions.isPending}
          className="w-full"
          data-testid="sessions-revoke-others-button"
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Sign out the other {otherSessionCount === 1 ? "session" : `${otherSessionCount} sessions`}
        </Button>
      )}

      <div className="space-y-3" data-testid="sessions-list">
        {sessions.map((session) => {
          const device = describeDevice(session.userAgent);
          const DeviceIcon = device.icon;
          return (
            <div
              key={session.id}
              className="rounded-md border p-4"
              data-testid={`session-row-${session.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 gap-3">
                  <DeviceIcon className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-medium">{device.label}</p>
                      {session.current && <Badge variant="success">This device</Badge>}
                      {session.mfaSatisfied && (
                        <Badge variant="outline" className="h-4 gap-1 px-1.5 text-[11px]">
                          <ShieldCheck className="h-3 w-3" />
                          Verified
                        </Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Active {formatRelative(session.updatedAt)}
                      </span>
                      {session.ipAddress && <span>· {session.ipAddress}</span>}
                    </div>
                  </div>
                </div>

                {!session.current && (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => setRevokeTarget({ id: session.id, label: device.label })}
                    disabled={revokeSession.isPending}
                    aria-label={`Sign out of ${device.label}`}
                    data-testid={`session-revoke-${session.id}`}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <AlertDialog
        open={revokeTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRevokeTarget(null);
        }}
      >
        <AlertDialogContent data-testid="sessions-revoke-one-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Sign out of {revokeTarget?.label}?</AlertDialogTitle>
            <AlertDialogDescription>
              That session will be ended right away. Whoever's using it will need to sign back in.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={revokeSession.isPending}
              data-testid="sessions-revoke-one-cancel-button"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (revokeTarget) revokeSession.mutate(revokeTarget.id);
              }}
              disabled={revokeSession.isPending}
              data-testid="sessions-revoke-one-confirm-button"
            >
              {revokeSession.isPending ? "Signing out…" : "Sign out"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmAllOpen} onOpenChange={setConfirmAllOpen}>
        <AlertDialogContent data-testid="sessions-revoke-all-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Sign out everywhere else?</AlertDialogTitle>
            <AlertDialogDescription>
              This device stays signed in. Every other browser, phone, and tab will be signed out
              right away.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={revokeOtherSessions.isPending}
              data-testid="sessions-revoke-all-cancel-button"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                revokeOtherSessions.mutate();
              }}
              disabled={revokeOtherSessions.isPending}
              data-testid="sessions-revoke-all-confirm-button"
            >
              {revokeOtherSessions.isPending ? "Signing out…" : "Sign out everywhere else"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
